import {
  templateFormWithoutPatientSchema,
  templateFormWithPatientSchema,
  type TemplateFormFields,
} from "@/pages/template-manager/TemplateForm/templateFormSchema";
import { logisticsApi } from "./ApiClient";
import { z } from "zod";
import {
  batchTemplateFormSchema,
  type BatchTemplateFormFields,
} from "@/pages/template-manager/BatchTemplateForm/batchTemplateFormSchema";
import {
  type ITagGroup,
  type ITemplateTagFields,
} from "@medoma/models/templates";
import { dateSchema } from "@medoma/models/date-and-time";
import * as Sentry from "@sentry/react";

/** –––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––
 * Query keys
 */

export const templatesKeys = {
  all: ["templates"] as const,
  details: (id: string, type: "activity" | "batch") =>
    [...templatesKeys.all, type, id] as const,
};

const tagsKeys = {
  all: ["templates", "tags"] as const,
  group: (group: ITagGroup) => [...tagsKeys.all, group] as const,
  details: (id: string) => [...tagsKeys.all, "details", id] as const,
};

/** –––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––
 * Schemas and types
 */

export const templateTagSchema = z.object({
  id: z.uuid(),
  displayName: z.string(),
  updatedAt: dateSchema.nullish(),
  deletedAt: dateSchema.nullish(),
});

const templateBaseSchema = z.object({
  templateId: z.uuid(),
  revisionNumber: z.number().int().positive(),
  tags: z.array(templateTagSchema),
  updatedAt: dateSchema,
});

export const templateAsStringSchema = templateBaseSchema.extend({
  template: z.string(),
});

// The backend nests the template itself as a JSON string inside the envelope.
// Only that string is pinned on the way in, so the envelope and the content are
// decoded together, in one pass, once the string has been JSON.parsed.
const backendTemplateSchema = z.looseObject({
  template: z.string(),
});

export type IMalformedTemplate = z.infer<typeof backendTemplateSchema>;

export const activityTemplateWithPatientSchema = templateBaseSchema.extend({
  template: templateFormWithPatientSchema,
});

export const activityTemplateWithoutPatientSchema = templateBaseSchema.extend({
  template: templateFormWithoutPatientSchema,
});

export const activityTemplateSchema = z.union([
  activityTemplateWithPatientSchema,
  activityTemplateWithoutPatientSchema,
]);

export const batchTemplateSchema = templateBaseSchema.extend({
  template: batchTemplateFormSchema,
});

export const templateSchema = z.union([
  activityTemplateSchema,
  batchTemplateSchema,
]);

export type ITemplateTag = z.infer<typeof templateTagSchema>;
export type IActivityTemplateWithPatient = z.infer<
  typeof activityTemplateWithPatientSchema
>;
export type IActivityTemplateWithoutPatient = z.infer<
  typeof activityTemplateWithoutPatientSchema
>;
export type IActivityTemplate = z.infer<typeof activityTemplateSchema>;
export type IBatchTemplate = z.infer<typeof batchTemplateSchema>;

/**
 * Helper type to exclude tag-related fields from template form data.
 * Uses ITemplateTagFields to ensure any new tag fields are automatically excluded.
 *
 * Batch template form data carries the tag fields and activity template form data
 * does not, so the keys to remove are intersected with the ones T actually has.
 */
type ExcludeTagFields<T> = StrictOmit<
  T,
  Extract<keyof T, keyof ITemplateTagFields>
> & {
  [K in keyof ITemplateTagFields]?: never;
};

/** –––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––
 * API endpoints TEMPLATES
 */

/**
 * Fetch all templates
 * @returns All templates grouped into activity templates and batch templates.
 * Also returns any malformed templates, so that they can be displayed to the user and dealt with accordingly.
 */
async function fetchTemplates() {
  const backendTemplates = await logisticsApi
    .get(`/templates`)
    .json(backendTemplateSchema.array());

  const parsedTemplates: z.infer<typeof templateSchema>[] = [];
  const malformedTemplates: IMalformedTemplate[] = [];

  for (const backendTemplate of backendTemplates) {
    try {
      const parsedTemplate = templateSchema.safeParse({
        ...backendTemplate,
        template: JSON.parse(backendTemplate.template),
      });
      if (parsedTemplate.success) {
        parsedTemplates.push(parsedTemplate.data);
      } else {
        malformedTemplates.push(backendTemplate);
      }
    } catch {
      malformedTemplates.push(backendTemplate);

      // We don't expect JSON parse to fail, but if it does, we want to know about it.
      Sentry.captureException(
        new Error(
          `Failed to parse template: ${String(backendTemplate.templateId)}`,
        ),
      );
    }
  }

  const activities = parsedTemplates.filter(
    (template): template is IActivityTemplate =>
      !("activities" in template.template),
  );

  const activitiesWithPatient = activities.filter(
    (activity): activity is IActivityTemplateWithPatient =>
      "patientType" in activity.template,
  );
  const activitiesWithoutPatient = activities.filter(
    (activity): activity is IActivityTemplateWithoutPatient =>
      "withoutPatient" in activity.template,
  );

  const batches = parsedTemplates.filter(
    (template): template is IBatchTemplate => "activities" in template.template,
  );

  return {
    activities: {
      withPatient: activitiesWithPatient,
      withoutPatient: activitiesWithoutPatient,
    },
    batches,
    malformedTemplates,
  };
}

async function fetchActivityTemplate(id: string) {
  const backendTemplate = await logisticsApi
    .get(`/templates/${id}`)
    .json(backendTemplateSchema);

  return activityTemplateSchema.parse({
    ...backendTemplate,
    template: JSON.parse(backendTemplate.template),
  });
}

async function fetchBatchTemplate(id: string) {
  const backendTemplate = await logisticsApi
    .get(`/templates/${id}`)
    .json(backendTemplateSchema);

  return batchTemplateSchema.parse({
    ...backendTemplate,
    template: JSON.parse(backendTemplate.template),
  });
}

export async function createActivityTemplate({
  formData,
  tagIds,
}: {
  formData: ExcludeTagFields<TemplateFormFields>;
  tagIds: string[];
}) {
  return await logisticsApi.post(`/templates`, {
    json: { template: formData, tags: tagIds },
  });
}

export async function createBatchTemplate({
  formData,
  tagIds,
}: {
  formData: ExcludeTagFields<BatchTemplateFormFields>;
  tagIds: string[];
}) {
  return await logisticsApi.post(`/templates`, {
    json: { template: formData, tags: tagIds },
  });
}

export async function updateActivityTemplate(
  id: string,
  formData: ExcludeTagFields<TemplateFormFields>,
) {
  return await logisticsApi.put(`/templates/${id}`, {
    json: { template: formData },
  });
}

export async function updateBatchTemplate(
  id: string,
  formData: ExcludeTagFields<BatchTemplateFormFields>,
) {
  return await logisticsApi.put(`/templates/${id}`, {
    json: { template: formData },
  });
}

export async function updateTagsForTemplate({
  id,
  tagIds,
}: {
  id: string;
  tagIds: string[];
}) {
  return await logisticsApi.put(`/templates/${id}/tags`, {
    json: { tags: tagIds },
  });
}

export async function deleteTemplate(id: string) {
  return await logisticsApi.delete(`/templates/${id}`);
}

/** –––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––
 * API endpoints TAGS
 */

async function fetchTags() {
  return await logisticsApi
    .get(`/templates/tags`)
    .json(z.array(templateTagSchema));
}

async function fetchTag(id: string) {
  const tag = (
    await logisticsApi.get(`/templates/tags`).json(z.array(templateTagSchema))
  ).find((tag) => tag.id === id);
  return tag ?? null;
}

export async function createTag(displayName: string) {
  return await logisticsApi.post(`/templates/tags`, {
    json: { displayName },
  });
}

export async function updateTag({
  id,
  newDisplayName,
}: {
  id: string;
  newDisplayName: string;
}) {
  return await logisticsApi.put(`/templates/tags/${id}`, {
    json: { displayName: newDisplayName },
  });
}

export async function deleteTag(id: string) {
  return await logisticsApi.delete(`/templates/tags/${id}`);
}

/** –––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––
 * Queries
 */

export const templatesQueryOptions = () => {
  return {
    queryKey: templatesKeys.all,
    queryFn: fetchTemplates,
  };
};

export const activityTemplateQueryOptions = (id: string) => {
  return {
    queryKey: templatesKeys.details(id, "activity"),
    queryFn: () => fetchActivityTemplate(id),
  };
};

export const batchTemplateQueryOptions = (id: string) => {
  return {
    queryKey: templatesKeys.details(id, "batch"),
    queryFn: () => fetchBatchTemplate(id),
  };
};

export const tagsByGroupQueryOptions = (group: ITagGroup) => {
  return {
    queryKey: tagsKeys.group(group),
    queryFn: async () => {
      const tags = await fetchTags();
      return tags.filter((tag) => tag.displayName.startsWith(`${group}:`));
    },
  };
};

export const tagQueryOptions = (id: string) => {
  return {
    queryKey: tagsKeys.details(id),
    queryFn: () => fetchTag(id),
  };
};
